if (!customElements.get('byg-selv-menu-form')) {
  class BygSelvMenuForm extends HTMLElement {
    constructor() {
      super();

      this.checkboxes = Array.from(this.querySelectorAll('[data-byg-selv-menu-variant]'));
      this.kuverterInput = this.querySelector('[data-byg-selv-menu-kuverter]');
      this.submitButton = this.querySelector('[data-byg-selv-menu-submit]');
      this.statusEl = this.querySelector('[data-byg-selv-menu-status]');
      this.errorEl = this.querySelector('[data-byg-selv-menu-error]');
      this.kuvertPriceEl = this.querySelector('[data-byg-selv-menu-kuvertpris]');

      this.minVariants = parseInt(this.dataset.minVariants, 10) || 1;
      this.minKuverter = parseInt(this.dataset.minKuverter, 10) || 1;
      this.cartAddUrl = this.dataset.cartAddUrl;

      this.lightboxTriggers = Array.from(this.querySelectorAll('[data-byg-selv-menu-lightbox]'));
      this.lightboxOverlay = this.querySelector('[data-byg-selv-menu-lightbox-overlay]');
      this.lightboxImage = this.querySelector('[data-byg-selv-menu-lightbox-image]');
      this.lightboxCloseButton = this.querySelector('[data-byg-selv-menu-lightbox-close]');

      this.onSubmit = this.onSubmit.bind(this);
      this.updateState = this.updateState.bind(this);
      this.openLightbox = this.openLightbox.bind(this);
      this.closeLightbox = this.closeLightbox.bind(this);
      this.onLightboxKeydown = this.onLightboxKeydown.bind(this);

      this.checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', this.updateState);
      });

      if (this.kuverterInput) {
        this.kuverterInput.addEventListener('change', this.updateState);
      }

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmit);
      }

      this.lightboxTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.openLightbox);
      });

      if (this.lightboxCloseButton) {
        this.lightboxCloseButton.addEventListener('click', this.closeLightbox);
      }

      if (this.lightboxOverlay) {
        this.lightboxOverlay.addEventListener('click', (event) => {
          if (event.target === this.lightboxOverlay) {
            this.closeLightbox();
          }
        });
      }
    }

    connectedCallback() {
      this.updateState();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onLightboxKeydown);
    }

    openLightbox(event) {
      const trigger = event.currentTarget;

      if (!this.lightboxOverlay || !this.lightboxImage) return;

      this.lightboxImage.src = trigger.dataset.lightboxSrc;
      this.lightboxImage.alt = trigger.dataset.lightboxAlt || '';
      this.lightboxOverlay.hidden = false;
      document.addEventListener('keydown', this.onLightboxKeydown);
    }

    closeLightbox() {
      if (!this.lightboxOverlay) return;

      this.lightboxOverlay.hidden = true;
      this.lightboxImage.src = '';
      document.removeEventListener('keydown', this.onLightboxKeydown);
    }

    onLightboxKeydown(event) {
      if (event.key === 'Escape') {
        this.closeLightbox();
      }
    }

    getSelectedCheckboxes() {
      return this.checkboxes.filter((checkbox) => checkbox.checked);
    }

    getKuverterValue() {
      return parseInt(this.kuverterInput?.value, 10) || 0;
    }

    getSelectedTotalCents() {
      return this.getSelectedCheckboxes().reduce((sum, checkbox) => sum + (parseInt(checkbox.dataset.price, 10) || 0), 0);
    }

    formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        const format = window.theme.moneyWithoutCurrencyFormat || window.theme.settings?.moneyFormat;
        return window.theme.formatMoney(cents, format);
      }

      return `${(cents / 100).toFixed(2).replace('.', ',')} kr.`;
    }

    updateState() {
      const selectedCount = this.getSelectedCheckboxes().length;
      const kuverterValue = this.getKuverterValue();
      const isValid = selectedCount >= this.minVariants && kuverterValue >= this.minKuverter;

      if (this.statusEl) {
        this.statusEl.textContent = `${selectedCount} af minimum ${this.minVariants} varianter valgt`;
      }

      if (this.kuvertPriceEl) {
        const totalOrderPrice = this.getSelectedTotalCents() * kuverterValue;
        const pricePerKuvert = kuverterValue > 0 ? totalOrderPrice / kuverterValue : 0;
        this.kuvertPriceEl.textContent = `Pris pr. kuvert: ${this.formatMoney(pricePerKuvert)}`;
      }

      if (this.submitButton) {
        this.submitButton.disabled = !isValid;
      }

      return isValid;
    }

    setError(message) {
      if (!this.errorEl) return;

      if (message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.setError(null);

      if (!this.updateState() || !this.cartAddUrl) return;

      const quantity = this.getKuverterValue();
      const items = this.getSelectedCheckboxes().map((checkbox) => ({
        id: parseInt(checkbox.value, 10),
        quantity,
      }));

      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');

      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.description || response.message || 'Der opstod en fejl. Prøv igen.');
          }

          this.checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
          });

          document.dispatchEvent(new CustomEvent('theme:cart:refresh'));

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
          }
        })
        .catch((error) => {
          this.setError(error.message || 'Kunne ikke tilføje menuen til kurven. Prøv igen.');
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          this.updateState();
        });
    }
  }

  customElements.define('byg-selv-menu-form', BygSelvMenuForm);
}
